/**
 * Countdown Timer Web Component
 *
 * Counts down to a specific date and time in a given timezone. When the
 * countdown reaches zero, the parent banner is hidden.
 *
 * Attributes:
 *   target-date     - Target date in YYYY-MM-DD format. Required; without it
 *                     the element removes itself on connect.
 *   target-hour     - Target hour in 24h format (0-23), default: 23
 *   target-minute   - Target minute (0-59), default: 59
 *   timezone        - IANA timezone string, default: "America/New_York"
 *   expired-text    - Text to show when expired, default: "0m 00s"
 *   banner-selector - CSS selector for the banner to hide on expiry. Falls
 *                     back to walking up to the nearest element matching
 *                     [class*="promo-banner"], [class*="announcement"], or
 *                     [class*="countdown-banner"].
 *   format          - Display format. Default (attribute absent): the legacy
 *                     "Xd XXh YYm ZZs" / "XXh YYm ZZs" / "YYm ZZs" output used
 *                     by the cart promo timer. "dd:hh:mm:ss": zero-padded,
 *                     colon-joined, always four segments (e.g. 01:05:57:23);
 *                     the day segment grows past two digits naturally.
 *   expiry-scope    - When "self", on expiry this element hides ITSELF only
 *                     (this.style.display='none') and does NOT touch any
 *                     ancestor or banner-selector. Default (absent): legacy
 *                     banner-hiding behavior used by the cart.
 *
 * Usage:
 *   <countdown-timer
 *     target-date="2026-04-30"
 *     target-hour="23"
 *     target-minute="59"
 *     timezone="America/New_York"
 *     banner-selector="#MyBanner">
 *     --:--
 *   </countdown-timer>
 */
class CountdownTimer extends HTMLElement {
  constructor() {
    super();
    this.animationFrameId = null;
    this.endTime = null;
  }

  connectedCallback() {
    this.initTimer();
  }

  disconnectedCallback() {
    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }
  }

  get targetDate() {
    return this.getAttribute('target-date') || null;
  }

  get targetHour() {
    return parseInt(this.getAttribute('target-hour')) || 23;
  }

  get targetMinute() {
    return parseInt(this.getAttribute('target-minute')) || 59;
  }

  get timezone() {
    return this.getAttribute('timezone') || 'America/New_York';
  }

  get expiredText() {
    return this.getAttribute('expired-text') || '0m 00s';
  }

  get bannerSelector() {
    return this.getAttribute('banner-selector') || null;
  }

  get format() {
    return this.getAttribute('format') || null;
  }

  get expiryScope() {
    return this.getAttribute('expiry-scope') || null;
  }

  initTimer() {
    // Without a target date, nothing to count down to. Hide immediately so
    // the banner shows only its text content.
    if (!this.targetDate) {
      this.style.display = 'none';
      return;
    }

    const dateParts = this.targetDate.split('-').map(Number);
    if (dateParts.length !== 3 || dateParts.some(isNaN)) {
      this.style.display = 'none';
      return;
    }
    const [targetYear, targetMonth, targetDay] = dateParts;

    // Read "now" as wall-clock in the target timezone, so we can subtract
    // target wall-clock from current wall-clock and get an elapsed-ms value
    // that doesn't depend on the visitor's browser timezone.
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: this.timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    });
    const values = {};
    formatter.formatToParts(new Date()).forEach((p) => {
      if (p.type !== 'literal') values[p.type] = parseInt(p.value);
    });

    // Some browsers return "24" instead of "0" for midnight with hour12:false.
    if (values.hour === 24) values.hour = 0;

    const targetPseudoLocal = new Date(
      targetYear,
      targetMonth - 1,
      targetDay,
      this.targetHour,
      this.targetMinute,
      0
    );
    const nowPseudoLocal = new Date(
      values.year,
      values.month - 1,
      values.day,
      values.hour,
      values.minute,
      values.second
    );

    const diffMs = targetPseudoLocal - nowPseudoLocal;
    if (diffMs <= 0) {
      this.textContent = this.expiredText;
      this.handleExpiry();
      return;
    }

    this.endTime = Date.now() + diffMs;
    this.updateTimer();
  }

  updateTimer() {
    const remaining = Math.max(0, this.endTime - Date.now());

    if (remaining <= 0) {
      this.textContent = this.expiredText;
      this.handleExpiry();
      return;
    }

    const days = Math.floor(remaining / 86400000);
    const hours = Math.floor((remaining % 86400000) / 3600000);
    const minutes = Math.floor((remaining % 3600000) / 60000);
    const seconds = Math.floor((remaining % 60000) / 1000);

    const mm = String(minutes).padStart(2, '0');
    const ss = String(seconds).padStart(2, '0');

    if (this.format === 'dd:hh:mm:ss') {
      // New announcement-bar format. Cart instances never set `format`, so the
      // legacy branches below run byte-identically for them.
      this.textContent = `${String(days).padStart(2, '0')}:${String(hours).padStart(2, '0')}:${mm}:${ss}`;
    } else if (days > 0) {
      this.textContent = `${days}d ${String(hours).padStart(2, '0')}h ${mm}m ${ss}s`;
    } else if (hours > 0) {
      this.textContent = `${hours}h ${mm}m ${ss}s`;
    } else {
      this.textContent = `${minutes}m ${ss}s`;
    }

    this.animationFrameId = requestAnimationFrame(() => this.updateTimer());
  }

  handleExpiry() {
    // Announcement-bar usage: hide only this element so the surrounding
    // pre-text / CTA / bar / slide stay. Returns before any banner logic;
    // cart instances never set `expiry-scope`, so their path is unchanged.
    if (this.expiryScope === 'self') {
      this.style.display = 'none';
      return;
    }

    let banner = null;

    if (this.bannerSelector) {
      banner = document.querySelector(this.bannerSelector);
    }

    if (!banner) {
      banner = this.closest(
        '[class*="promo-banner"], [class*="announcement"], [class*="countdown-banner"]'
      );
    }

    if (banner) {
      banner.style.display = 'none';
    }
  }
}

if (!customElements.get('countdown-timer')) {
  customElements.define('countdown-timer', CountdownTimer);
}
